import { ApplicationController } from "../../support/application-controller";

/**
 * Stimulus controller for handling HubSpot missing mappings feature.
 * Allows admins to analyze and create missing HubSpot external mappings
 * (contact, deal) for subscriptions that already have a HubSpot subscription mapping.
 */
export default class extends ApplicationController {
    static targets = [
        "modal",
        "modalContent",
        "loadingState",
        "errorState",
        "errorMessage",
        "formState",
        "mappingCheckbox",
        "submitButton",
        "hubspotIdInput"
    ];

    connect() {
        console.log("[HubSpot Missing Mappings] Controller connected");
        console.log("[HubSpot Missing Mappings] Element:", this.element);
        console.log("[HubSpot Missing Mappings] Dataset:", this.element.dataset);

        this.analysisData = null;
        // Read data attributes using Stimulus 1.x API
        this.analyzeUrl = this.data.get("analyzeUrl");
        this.createUrl = this.data.get("createUrl");
        this.subscriptionId = this.data.get("subscriptionId");
        this.hubspotSubscriptionId = this.data.get("hubspotSubscriptionId");

        console.log("[HubSpot Missing Mappings] analyzeUrl:", this.analyzeUrl);
        console.log("[HubSpot Missing Mappings] createUrl:", this.createUrl);
    }

    /**
     * Opens the modal and fetches available mappings from HubSpot
     */
    openModal(event) {
        console.log("[HubSpot Missing Mappings] openModal called");
        console.log("[HubSpot Missing Mappings] hasModalTarget:", this.hasModalTarget);
        console.log("[HubSpot Missing Mappings] analyzeUrl:", this.analyzeUrl);
        var self = this;
        event.preventDefault();

        // Show modal with loading state
        console.log("[HubSpot Missing Mappings] Calling showModal()");
        this.showModal();
        console.log("[HubSpot Missing Mappings] Calling showLoadingState()");
        this.showLoadingState();

        console.log("[HubSpot Missing Mappings] Calling fetchAnalysis()");
        this.fetchAnalysis()
            .then(function(response) {
                if (response.success) {
                    self.analysisData = response;
                    self.renderForm(response);
                    self.showFormState();
                } else {
                    self.showError(response.error || "Failed to analyze mappings");
                }
            })
            .catch(function(error) {
                console.error("Error analyzing HubSpot mappings:", error);
                self.showError(error.message || "Failed to fetch HubSpot data");
            });
    }

    /**
     * Closes the modal
     */
    closeModal(event) {
        if (event) event.preventDefault();
        this.hideModal();
        this.analysisData = null;
    }

    /**
     * Closes the modal if clicking on the backdrop
     */
    closeOnBackdrop(event) {
        if (event.target === this.modalTarget) {
            this.closeModal(event);
        }
    }

    /**
     * Handles form submission to create selected mappings
     */
    submitMappings(event) {
        var self = this;
        event.preventDefault();

        var selectedMappings = this.getSelectedMappings();
        if (selectedMappings.length === 0) {
            this.showError("Please select at least one mapping to create");
            return;
        }

        var hubspotSubscriptionId = this.getHubspotSubscriptionId();
        if (!hubspotSubscriptionId) {
            this.showError("HubSpot subscription ID is required");
            return;
        }

        this.disableSubmit();

        this.createMappings(hubspotSubscriptionId, selectedMappings)
            .then(function(response) {
                if (response.success) {
                    // Refresh the page to show updated mappings
                    window.location.reload();
                } else {
                    self.showError(response.error || "Failed to create mappings");
                    self.enableSubmit();
                }
            })
            .catch(function(error) {
                console.error("Error creating HubSpot mappings:", error);
                self.showError(error.message || "Failed to create mappings");
                self.enableSubmit();
            });
    }

    /**
     * Updates the submit button state based on checkbox selection
     */
    updateSubmitState() {
        var selectedCount = this.getSelectedMappings().length;
        if (this.hasSubmitButtonTarget) {
            this.submitButtonTarget.disabled = selectedCount === 0;
            this.submitButtonTarget.textContent = selectedCount === 0
                ? "Select mappings to create"
                : "Create " + selectedCount + " mapping" + (selectedCount > 1 ? 's' : '');
        }
    }

    // Private methods

    fetchAnalysis() {
        var self = this;
        console.log("[HubSpot Missing Mappings] fetchAnalysis - analyzeUrl:", this.analyzeUrl);
        var url = new URL(this.analyzeUrl, window.location.origin);
        if (this.hubspotSubscriptionId) {
            url.searchParams.set('hubspot_subscription_id', this.hubspotSubscriptionId);
        }
        console.log("[HubSpot Missing Mappings] fetchAnalysis - full URL:", url.toString());

        return fetch(url.toString(), {
            method: 'GET',
            headers: {
                'Accept': 'application/json',
                'X-CSRF-Token': this.getCSRFToken()
            },
            credentials: 'same-origin'
        }).then(function(response) {
            if (!response.ok) {
                return response.json()
                    .catch(function() { return {}; })
                    .then(function(errorData) {
                        throw new Error(errorData.error || "HTTP " + response.status);
                    });
            }
            return response.json();
        });
    }

    createMappings(hubspotSubscriptionId, mappingTypes) {
        var self = this;
        return fetch(this.createUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
                'X-CSRF-Token': this.getCSRFToken()
            },
            credentials: 'same-origin',
            body: JSON.stringify({
                hubspot_subscription_id: hubspotSubscriptionId,
                mapping_types: mappingTypes
            })
        }).then(function(response) {
            if (!response.ok) {
                return response.json()
                    .catch(function() { return {}; })
                    .then(function(errorData) {
                        throw new Error(errorData.error || "HTTP " + response.status);
                    });
            }
            return response.json();
        });
    }

    getSelectedMappings() {
        if (!this.hasMappingCheckboxTarget) return [];
        return this.mappingCheckboxTargets
            .filter(function(checkbox) { return checkbox.checked; })
            .map(function(checkbox) { return checkbox.value; });
    }

    getHubspotSubscriptionId() {
        if (this.hasHubspotIdInputTarget && this.hubspotIdInputTarget.value) {
            return this.hubspotIdInputTarget.value;
        }
        return this.hubspotSubscriptionId || (this.analysisData && this.analysisData.hubspot_subscription_id);
    }

    renderForm(data) {
        var existing = data.existing;
        var available = data.available;
        var hubspot_data = data.hubspot_data;

        var html = '\
            <div class="hubspot-mappings-form">\
                <p style="margin-bottom: 16px; color: #666;">\
                    Select which HubSpot mappings to create for this subscription:\
                </p>\
        ';

        // HubSpot Subscription ID (hidden or editable if not existing)
        if (!existing.subscription && available.subscription.available) {
            html += '\
                <div class="hubspot-mappings-form__item hubspot-mappings-form__item--editable">\
                    <label class="hubspot-mappings-form__checkbox-label">\
                        <input type="checkbox"\
                               data-target="admin--hubspot-missing-mappings.mappingCheckbox"\
                               data-action="change->admin--hubspot-missing-mappings#updateSubmitState"\
                               value="subscription"\
                               checked>\
                        <span class="hubspot-mappings-form__label-text">\
                            <strong>Subscription</strong>\
                            <span class="hubspot-mappings-form__label-detail">\
                                ID: ' + available.subscription.external_id +
                                (available.subscription.name ? ' - ' + available.subscription.name : '') + '\
                            </span>\
                        </span>\
                    </label>\
                </div>\
            ';
        }

        // Contact mapping
        html += this.renderMappingOption(
            'contact',
            'Contact',
            existing.contact,
            available.contact,
            hubspot_data.contact
        );

        // User Deal mapping
        html += this.renderMappingOption(
            'user_deal',
            'User → Deal',
            existing.user_deal,
            available.deal,
            hubspot_data.deal
        );

        // Organization Deal mapping (if applicable)
        if (existing.org_deal !== null) {
            html += this.renderMappingOption(
                'org_deal',
                'Organization → Deal',
                existing.org_deal,
                available.deal,
                hubspot_data.deal
            );
        }

        html += '\
            </div>\
            <div class="hubspot-mappings-form__actions">\
                <button type="button"\
                        class="button-flat"\
                        data-action="click->admin--hubspot-missing-mappings#closeModal">\
                    Cancel\
                </button>\
                <button type="button"\
                        class="button-flat button-flat--primary"\
                        data-target="admin--hubspot-missing-mappings.submitButton"\
                        data-action="click->admin--hubspot-missing-mappings#submitMappings">\
                    Create selected mappings\
                </button>\
            </div>\
        ';

        if (this.hasModalContentTarget) {
            this.modalContentTarget.innerHTML = html;
        }

        // Update submit button state
        this.updateSubmitState();
    }

    renderMappingOption(type, label, exists, available, hubspotData) {
        if (exists) {
            return '\
                <div class="hubspot-mappings-form__item hubspot-mappings-form__item--exists">\
                    <span class="hubspot-mappings-form__status hubspot-mappings-form__status--exists">✓</span>\
                    <span class="hubspot-mappings-form__label-text">\
                        <strong>' + label + '</strong>\
                        <span class="hubspot-mappings-form__label-detail">Already mapped</span>\
                    </span>\
                </div>\
            ';
        }

        if (!available || !available.available) {
            return '\
                <div class="hubspot-mappings-form__item hubspot-mappings-form__item--unavailable">\
                    <span class="hubspot-mappings-form__status hubspot-mappings-form__status--unavailable">—</span>\
                    <span class="hubspot-mappings-form__label-text">\
                        <strong>' + label + '</strong>\
                        <span class="hubspot-mappings-form__label-detail">Not available in HubSpot</span>\
                    </span>\
                </div>\
            ';
        }

        var detail = this.getDetailText(type, hubspotData);
        return '\
            <div class="hubspot-mappings-form__item">\
                <label class="hubspot-mappings-form__checkbox-label">\
                    <input type="checkbox"\
                           data-target="admin--hubspot-missing-mappings.mappingCheckbox"\
                           data-action="change->admin--hubspot-missing-mappings#updateSubmitState"\
                           value="' + type + '"\
                           checked>\
                    <span class="hubspot-mappings-form__label-text">\
                        <strong>' + label + '</strong>\
                        <span class="hubspot-mappings-form__label-detail">\
                            ID: ' + available.external_id + (detail ? ' - ' + detail : '') + '\
                        </span>\
                    </span>\
                </label>\
            </div>\
        ';
    }

    getDetailText(type, hubspotData) {
        if (!hubspotData) return '';
        switch (type) {
            case 'contact':
                return hubspotData.email || hubspotData.name || '';
            case 'user_deal':
            case 'org_deal':
                return hubspotData.name || '';
            default:
                return '';
        }
    }

    showModal() {
        console.log("[HubSpot Missing Mappings] showModal - hasModalTarget:", this.hasModalTarget);
        if (this.hasModalTarget) {
            console.log("[HubSpot Missing Mappings] showModal - adding visible class");
            this.modalTarget.classList.add('hubspot-mappings-modal--visible');
            document.body.style.overflow = 'hidden';
        } else {
            console.warn("[HubSpot Missing Mappings] showModal - NO MODAL TARGET FOUND!");
        }
    }

    hideModal() {
        if (this.hasModalTarget) {
            this.modalTarget.classList.remove('hubspot-mappings-modal--visible');
            document.body.style.overflow = '';
        }
    }

    showLoadingState() {
        if (this.hasLoadingStateTarget) this.loadingStateTarget.style.display = 'block';
        if (this.hasErrorStateTarget) this.errorStateTarget.style.display = 'none';
        if (this.hasFormStateTarget) this.formStateTarget.style.display = 'none';
    }

    showFormState() {
        if (this.hasLoadingStateTarget) this.loadingStateTarget.style.display = 'none';
        if (this.hasErrorStateTarget) this.errorStateTarget.style.display = 'none';
        if (this.hasFormStateTarget) this.formStateTarget.style.display = 'block';
    }

    showError(message) {
        if (this.hasLoadingStateTarget) this.loadingStateTarget.style.display = 'none';
        if (this.hasFormStateTarget) this.formStateTarget.style.display = 'none';
        if (this.hasErrorStateTarget) {
            this.errorStateTarget.style.display = 'block';
            if (this.hasErrorMessageTarget) {
                this.errorMessageTarget.textContent = message;
            }
        }
    }

    disableSubmit() {
        if (this.hasSubmitButtonTarget) {
            this.submitButtonTarget.disabled = true;
            this.submitButtonTarget.textContent = 'Creating...';
        }
    }

    enableSubmit() {
        this.updateSubmitState();
    }

    getCSRFToken() {
        var meta = document.querySelector('meta[name="csrf-token"]');
        return meta ? meta.getAttribute('content') : '';
    }
}
